import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * CourseCrawler
 * -----------------------------------------------------------
 * - Crawls folders/modules/lessons (trusts hasChildren)
 * - Shows body/instructions
 * - Maps grade columns by contentId and gradeColumnId
 * - Auto-loads and shows ALL questions for Ultra assessments/tests
 */
const CourseCrawler = ({ platformUrl }) => {
    const qs = new URLSearchParams(window.location.search);
    const token = qs.get("token");
    const courseUuid = qs.get("courseUuid");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [items, setItems] = useState([]);
    const [gradeMap, setGradeMap] = useState({});
    const [expanded, setExpanded] = useState({ ROOT: true });

    // Prefetched questions cache: assessmentId -> { loading, error, questions[] }
    const [questionsMap, setQuestionsMap] = useState({});

    const authHeaders = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    const typeLabel = (contentHandlerId) => {
        if (!contentHandlerId) return "Content";
        const id = String(contentHandlerId).toLowerCase();
        if (id.includes("externallink")) return "Web Link";
        if (id.includes("folder")) return "Folder";
        if (id.includes("learningmodule")) return "Learning Module";
        if (id.includes("lesson")) return "Lesson";
        if (id.includes("asmt-test-link") || (id.includes("test") && !id.includes("assignment"))) return "Test";
        if (id.includes("asmt") || id.includes("assignment")) return "Assignment";
        if (id.includes("document")) return "Document";
        return contentHandlerId;
    };

    const fmtDate = (d) => {
        try {
            if (!d) return "";
            const dt = new Date(d);
            if (isNaN(dt)) return String(d);
            return dt.toLocaleString();
        } catch {
            return String(d) || "";
        }
    };

    const isWebLink = (item) => item?.contentHandler?.id === "resource/x-bb-externallink";
    const isDocument = (item) => item?.contentHandler?.id === "resource/x-bb-document";
    const isAssessmentLike = (item) => {
        const idStr = (item?.contentHandler?.id || "").toLowerCase();
        return idStr.includes("asmt-test-link") || idStr.includes("test") || idStr.includes("assignment") || idStr.includes("asmt");
    };

    const isContainerHandler = (handlerId = "") => {
        const id = String(handlerId || "").toLowerCase();
        return id.includes("folder") || id.includes("learningmodule") || id.includes("lesson") || id.includes("module");
    };

    const getPaged = async (url) => {
        let results = [];
        let next = url;
        while (next) {
            const { data } = await axios.get(next, authHeaders);
            results = results.concat(data?.results || []);
            next = data?.paging?.nextPage || null;
        }
        return results;
    };

    const fetchRootContents = async () => {
        const base = `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}/contents?parentId=ROOT&limit=200`;
        return await getPaged(base);
    };

    const fetchChildren = async (contentId) => {
        const base = `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}/contents/${contentId}/children?limit=200`;
        return await getPaged(base);
    };

    /**
     * Grade columns: index by contentId and by grade column id
     */
    const fetchGradebookColumns = async () => {
        const base = `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}/gradebook/columns?limit=200`;
        const cols = await getPaged(base);
        const map = {};
        for (const c of cols) {
            const info = {
                pointsPossible: c?.score?.possible ?? c?.possible ?? null,
                dueDate: c?.dueDate ?? c?.grading?.due ?? null,
            };
            if (c?.contentId) map[c.contentId] = info;
            if (c?.id)       map[c.id]        = info;
        }
        return map;
    };

    const crawl = async () => {
        const all = [];
        const queue = [{ id: "ROOT", parentId: null, _root: true }];

        const rootChildren = await fetchRootContents();
        for (const ch of rootChildren) {
            all.push({ ...ch, parentId: "ROOT" });
            queue.push({
                id: ch.id,
                parentId: "ROOT",
                handlerId: ch?.contentHandler?.id || "",
                hasChildren: !!ch?.hasChildren,
            });
        }

        while (queue.length > 0) {
            const node = queue.shift();
            if (!node || node._root) continue;

            const cur = all.find(i => i.id === node.id);
            const nodeHandlerId = node.handlerId || cur?.contentHandler?.id || "";
            const nodeHasChildren = (node.hasChildren ?? cur?.hasChildren ?? false);

            if (!(nodeHasChildren || isContainerHandler(nodeHandlerId))) continue;

            try {
                const kids = await fetchChildren(node.id);
                for (const k of kids) {
                    all.push({ ...k, parentId: node.id });
                    const kidHandler = k?.contentHandler?.id || "";
                    queue.push({
                        id: k.id,
                        parentId: node.id,
                        handlerId: kidHandler,
                        hasChildren: !!k?.hasChildren,
                    });
                }
            } catch (e) {
                console.warn("Children fetch failed for", node.id, e?.message);
            }
        }

        return all;
    };

    /**
     * Questions
     */
    const fetchAssessmentQuestions = async (assessmentId) => {
        if (!assessmentId) return [];
        const base = `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}/assessments/${encodeURIComponent(assessmentId)}/questions?limit=200`;
        return await getPaged(base);
    };

    // Soft concurrency limiter to avoid stampeding the API
    const mapWithLimit = async (arr, limit, fn) => {
        const ret = new Array(arr.length);
        let i = 0;
        const workers = new Array(Math.min(limit, arr.length)).fill(0).map(async () => {
            while (i < arr.length) {
                const idx = i++;
                ret[idx] = await fn(arr[idx], idx);
            }
        });
        await Promise.all(workers);
        return ret;
    };

    // Load items + grade map, then prefetch ALL questions for every assessmentId
    useEffect(() => {
        (async () => {
            if (!token || !courseUuid) {
                setError("Missing access token or course UUID. Please provide both.");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const [list, gmap] = await Promise.all([crawl(), fetchGradebookColumns()]);
                setItems(list);
                setGradeMap(gmap);

                // Collect unique assessmentIds and prefetch questions for all
                const ids = Array.from(
                    new Set(
                        list
                            .map(it => it?.contentHandler?.assessmentId)
                            .filter(Boolean)
                    )
                );

                if (ids.length) {
                    // mark all as loading up front
                    setQuestionsMap(prev => ({
                        ...prev,
                        ...Object.fromEntries(ids.map(id => [id, { loading: true, error: "", questions: [] }]))
                    }));

                    const results = await mapWithLimit(ids, 4, async (aid) => {
                        try {
                            const qs = await fetchAssessmentQuestions(aid);
                            return [aid, { loading: false, error: "", questions: qs || [] }];
                        } catch (e) {
                            return [aid, { loading: false, error: e?.response?.data?.message || e?.message || "Failed to load questions.", questions: [] }];
                        }
                    });

                    setQuestionsMap(prev => ({ ...prev, ...Object.fromEntries(results) }));
                }
            } catch (e) {
                setError(e?.response?.data?.message || e?.message || "Failed to load course contents.");
            } finally {
                setLoading(false);
            }
        })();
    }, [platformUrl, courseUuid, token]); // eslint-disable-line react-hooks/exhaustive-deps

    const tree = useMemo(() => {
        const byParent = items.reduce((acc, it) => {
            const pid = it.parentId || "ROOT";
            (acc[pid] ||= []).push(it);
            return acc;
        }, {});
        Object.values(byParent).forEach(arr =>
            arr.sort(
                (a, b) =>
                    (a.position ?? 0) - (b.position ?? 0) ||
                    String(a.title || "").localeCompare(String(b.title || ""))
            )
        );
        return byParent;
    }, [items]);

    const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));
    const expandAll = () => {
        const s = { ...expanded };
        for (const it of items) s[it.id] = true;
        s.ROOT = true;
        setExpanded(s);
    };
    const collapseAll = () => setExpanded({ ROOT: true });

    if (loading) return <div className="bb-container">Loading course tree…</div>;
    if (error) return (
        <div className="bb-container bb-error">
            <h1 className="bb-title">Course Crawler</h1>
            <p>{error}</p>
        </div>
    );

    // Pretty rendering for Ultra FillInTheBlank anchors
    const emphasizeBlanks = (html) => {
        if (!html) return html;
        return html.replace(
            /<a[^>]*data-bbtype=["']customClass["'][^>]*>([\s\S]*?)<\/a>/gi,
            (_m, label) => `<span class="bb-blank">${label || "[BLANK]"}</span>`
        );
    };

    const normalizeQuestion = (q) => {
        const handler = q?.questionHandler || q?.handler || {};
        const type = String(handler?.type || q?.type || "question");
        const points = q?.pointsPossible ?? q?.points ?? null;

        const textHtml =
            q?.text ??
            q?.questionText ??
            handler?.text ??
            q?.prompt ??
            q?.body?.text ??
            "";

        const rawChoices =
            q?.answers ??
            q?.choices ??
            handler?.answers ??
            q?.options ??
            [];

        const choices = Array.isArray(rawChoices)
            ? rawChoices.map((c, i) => ({
                id: c?.id ?? String(i),
                text: c?.text ?? c?.label ?? c?.value ?? "",
                correct:
                    c?.isCorrect ??
                    c?.correct ??
                    (typeof c?.weight === "number" ? c.weight > 0 : undefined),
            }))
            : [];

        return { type, points, textHtml, choices };
    };

    const Row = ({ item, depth }) => {
        const id = item.id;
        const kids = tree[id] || [];
        const hasKids = kids.length > 0;
        const open = expanded[id] ?? (depth === 0);

        const handlerId = item?.contentHandler?.id;
        const label = typeLabel(handlerId);

        // Prefer mapping by content item id; fall back to gradeColumnId (e.g., asmt-test-link)
        const gradeColumnId = item?.contentHandler?.gradeColumnId;
        const gm = gradeMap[id] || (gradeColumnId ? gradeMap[gradeColumnId] : null) || null;

        const availability = item?.availability?.available;
        const visibilityNote = availability === "PartiallyVisible" ? " (partially visible)" : availability === "No" ? " (hidden)" : "";

        // Questions (already prefetched)
        const assessmentId = item?.contentHandler?.assessmentId || null;
        const qState = assessmentId ? questionsMap[assessmentId] : undefined;

        const QuestionBlock = ({ q, idx }) => {
            const nq = normalizeQuestion(q);
            return (
                <div className="bb-question" style={{ margin: "8px 0", padding: "8px 12px", border: "1px solid #ddd", borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Q{idx + 1}: {nq.type}{typeof nq.points === "number" ? ` · ${nq.points} pts` : ""}
                    </div>
                    {nq.textHtml ? (
                        <div style={{ fontSize: "0.95em" }} dangerouslySetInnerHTML={{ __html: emphasizeBlanks(nq.textHtml) }} />
                    ) : (
                        <div style={{ fontSize: "0.95em", opacity: 0.8 }}>(no prompt text)</div>
                    )}
                    {Array.isArray(nq.choices) && nq.choices.length > 0 && (
                        <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                            {nq.choices.map((c) => (
                                <li key={c.id}>
                                    {c.correct === true ? "✓ " : c.correct === false ? "○ " : "• "}
                                    <span dangerouslySetInnerHTML={{ __html: c.text || "" }} />
                                </li>
                            ))}
                        </ul>
                    )}
                    {!nq.textHtml && (!nq.choices || nq.choices.length === 0) && (
                        <details style={{ marginTop: 6 }}>
                            <summary style={{ cursor: "pointer" }}>Raw</summary>
                            <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(q, null, 2)}</pre>
                        </details>
                    )}
                </div>
            );
        };

        return (
            <>
                <tr>
                    <td style={{ paddingLeft: depth * 16, verticalAlign: "top" }}>
                        {hasKids && (
                            <button
                                className="bb-btn bb-btn-icon"
                                onClick={() => toggle(id)}
                                aria-label={open ? "Collapse" : "Expand"}
                            >
                                {open ? "▾" : "▸"}
                            </button>
                        )}
                        {!hasKids && <span style={{ display: "inline-block", width: 20 }} />}
                        <span className="bb-strong">{item.title || "(Untitled)"}</span>
                        <span style={{ marginLeft: 6, fontSize: "0.85em", opacity: 0.7 }}>{visibilityNote}</span>

                        {isDocument(item) && item?.body && (
                            <div style={{ marginTop: 4, fontSize: "0.9em" }}>
                                <div dangerouslySetInnerHTML={{ __html: item.body }} />
                            </div>
                        )}

                        {isAssessmentLike(item) && item?.contentHandler?.instructions && (
                            <div style={{ marginTop: 4, fontSize: "0.9em" }}>
                                <div dangerouslySetInnerHTML={{ __html: item.contentHandler.instructions }} />
                            </div>
                        )}

                        {assessmentId && (
                            <div style={{ marginTop: 8 }}>
                                {!qState && <div>Loading questions…</div>}
                                {qState?.loading && <div>Loading questions…</div>}
                                {qState?.error && <div className="bb-error">{qState.error}</div>}
                                {qState?.questions?.length > 0 && qState.questions.map((q, i) => (
                                    <QuestionBlock key={q.id || i} q={q} idx={i} />
                                ))}
                                {qState && !qState.loading && !qState.error && (!qState.questions || qState.questions.length === 0) && (
                                    <div style={{ opacity: 0.8 }}>No questions found.</div>
                                )}
                            </div>
                        )}
                    </td>
                    <td>{label}</td>
                    <td>{gm?.pointsPossible != null ? gm.pointsPossible : ""}</td>
                    <td>{gm?.dueDate ? fmtDate(gm.dueDate) : ""}</td>
                    <td /> {/* web link column unused for assessments */}
                </tr>
                {hasKids && open && kids.map((k) => (
                    <Row key={k.id} item={k} depth={depth + 1} />
                ))}
            </>
        );
    };

    const rootKids = tree["ROOT"] || [];

    return (
        <div className="bb-container">
            <div className="bb-header">
                <h1 className="bb-title">Course Crawler</h1>
                <div className="bb-actions" style={{ display: "flex", gap: 8 }}>
                    <button className="bb-btn" onClick={expandAll}>Expand all</button>
                    <button className="bb-btn" onClick={collapseAll}>Collapse all</button>
                </div>
            </div>

            <table className="bb-table">
                <thead>
                <tr>
                    <th style={{ width: "40%" }}>Title / Body / Instructions / Questions</th>
                    <th style={{ width: "18%" }}>Type</th>
                    <th style={{ width: "12%" }}>Points</th>
                    <th style={{ width: "18%" }}>Due Date</th>
                    <th style={{ width: "12%" }}>Web Link</th>
                </tr>
                </thead>
                <tbody>
                {(tree["ROOT"] || []).length === 0 ? (
                    <tr>
                        <td colSpan={5}>No content found in this course.</td>
                    </tr>
                ) : (
                    (tree["ROOT"] || []).map((it) => <Row key={it.id} item={it} depth={0} />)
                )}
                </tbody>
            </table>
        </div>
    );
};

export default CourseCrawler;
